import React, { useEffect, useState } from 'react';

interface ConfettiBurstProps {
  trigger: boolean;
  duration?: number;
}

/** Lightweight confetti burst on success. */
export const ConfettiBurst: React.FC<ConfettiBurstProps> = ({
  trigger,
  duration = 1500,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), duration);
    return () => clearTimeout(t);
  }, [trigger, duration]);

  if (!visible || !trigger) return null;

  const colors = ['#22c55e', '#eab308', '#3b82f6', '#ef4444', '#a855f7'];
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    deg: (i / 20) * 360,
    color: colors[i % colors.length],
    size: 6 + (i % 3) * 2,
    delay: i * 0.03,
  }));

  return (
    <div
      className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center"
      aria-hidden
    >
      <div className="relative w-4 h-4">
        {particles.map((p) => {
          const rad = (p.deg * Math.PI) / 180;
          const endX = Math.sin(rad) * 100;
          const endY = -Math.cos(rad) * 100;
          return (
            <div
              key={p.id}
              className="absolute left-1/2 top-1/2 -ml-[3px] -mt-[3px] rounded-full"
              style={{
                width: p.size,
                height: p.size,
                backgroundColor: p.color,
                animation: `confettiOut 0.9s ease-out forwards`,
                animationDelay: `${p.delay}s`,
                opacity: 0,
                ['--end-x' as string]: `${endX}px`,
                ['--end-y' as string]: `${endY}px`,
              }}
            />
          );
        })}
      </div>
      <style>{`
        @keyframes confettiOut {
          0% { opacity: 1; transform: translate(0, 0); }
          100% { opacity: 0; transform: translate(var(--end-x, 50px), var(--end-y, -50px)); }
        }
      `}</style>
    </div>
  );
};
